import React, { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import managedEntityApi, { type ManagedEntityRecord } from '../services/managedEntityApi';
import type { ServiceProviderRecord } from '../services/managedEntityApi';
import { useUser } from './UserContext';
import authService from '../services/authService';
import { classifyApiFailure, isRetryableApiFailure, isAuthFailure, type ApiFailureKind } from '../utils/apiFailure';
import { shouldAttemptReauth, markReauthAttempted, resetReauthAttempts } from '../utils/reauthAttempt';
import { canAccessFeature, getEffectivePlan } from '../types/PlanFeatures';
import type { FeatureId } from '../types/PlanFeatures';
import type { VenueRecord } from '../services/venueApi';
import type { PerformerRecord } from '../services/performerApi';

interface ManagedEntityContextType {
    currentEntity: ManagedEntityRecord | null;
    currentProfile: VenueRecord | PerformerRecord | ServiceProviderRecord | null;
    entities: ManagedEntityRecord[];
    userEntities: ManagedEntityRecord[];
    isLoading: boolean;
    /** False until the first fetch completes; stays true forever after.
     *  Lets parents gate top-level loading spinners on initial-load only,
     *  so background refetches don't unmount/remount the rendered tree
     *  (which loops if the unmounted child triggered the refetch). */
    hasLoadedOnce: boolean;
    error: string | null;
    /** How the last fetch failed, so a consumer can tell the server refusing
     *  this session from an API that is still booting — indistinguishable at a
     *  bare `catch`, and the reason one screen served both. `null` whenever
     *  `error` is null. */
    errorKind: ApiFailureKind | null;
    refreshEntities: () => Promise<void>;
    switchEntity: (entityId: string) => void;
    setCurrentEntity: (entity: ManagedEntityRecord | null) => void;
    hasFeature: (feature: FeatureId) => boolean;
}





/** Backoff before the single retry of a failed workspace fetch. */
const RETRY_DELAY_MS = 500;

const ManagedEntityContext = createContext<ManagedEntityContextType | undefined>(undefined);

export const ManagedEntityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentEntity, setCurrentEntity] = useState<ManagedEntityRecord | null>(null);
    const [currentProfile, setCurrentProfile] = useState<VenueRecord | PerformerRecord | ServiceProviderRecord | null>(null);
    const [userEntities, setUserEntities] = useState<ManagedEntityRecord[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [errorKind, setErrorKind] = useState<ApiFailureKind | null>(null);
    const { isAuthenticated, loading: userLoading } = useUser();

    // Effect to derive currentProfile from currentEntity
    useEffect(() => {
        if (!currentEntity) {
            setCurrentProfile(null);
            return;
        }

        if (currentEntity.type === 'venue' && currentEntity.managedVenue && currentEntity.managedVenue.length > 0) {
            setCurrentProfile(currentEntity.managedVenue[0]);
        } else if (currentEntity.type === 'performer' && currentEntity.managedPerformer && currentEntity.managedPerformer.length > 0) {
            setCurrentProfile(currentEntity.managedPerformer[0]);
        } else if (currentEntity.type === 'service_provider' && currentEntity.managedServiceProvider && currentEntity.managedServiceProvider.length > 0) {
            setCurrentProfile(currentEntity.managedServiceProvider[0]);
        } else {
            setCurrentProfile(null);
        }
    }, [currentEntity]);

    /** The in-flight `GET /api/managed-entities`, if any.
     *
     *  Concurrent callers join it rather than issuing a second identical
     *  request: the auth effect below fires as `isAuthenticated` flips, and
     *  several flows call `refreshEntities()` in the same tick (AdminLogin
     *  after accepting a team invite, WorkspaceCreate after provisioning).
     *  The coalescing window is exactly one request — a refresh issued after
     *  the previous one settled always fetches fresh, so the post-mutation
     *  `await api.update(...); refreshEntities()` pattern still sees its own
     *  write. */
    const inFlight = useRef<Promise<void> | null>(null);

    const fetchEntities = useCallback((): Promise<void> => {
        if (inFlight.current) return inFlight.current;

        const run = async (): Promise<void> => {
            try {
                setIsLoading(true);
                setError(null);
                setErrorKind(null);

                // One retry, on a FAILED request only (transient network blip
                // or a cold API instance). An empty list is deliberately NOT
                // retried: a registered user with no workspace is a first-class
                // state (Admin renders ClaimLimbo for it), so retrying "empty"
                // fired a second identical GET on every load for exactly those
                // users. The auth race the empty-retry originally papered over
                // is handled properly by the `userLoading` guard in the effect
                // below.
                //
                // The retry is also narrowed by FAILURE KIND: only a network
                // blip or a 5xx could plausibly answer differently a moment
                // later. A 401 retried is a second 401 — it delays the login
                // redirect by RETRY_DELAY_MS and buys nothing.
                let entities: ManagedEntityRecord[];
                try {
                    entities = await managedEntityApi.list({ timestamp: Date.now() });
                } catch (err) {
                    if (!isRetryableApiFailure(classifyApiFailure(err))) throw err;
                    if (import.meta.env.DEV) {
                        console.warn('[ManagedEntityContext] Workspace fetch failed, retrying once', err);
                    }
                    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
                    entities = await managedEntityApi.list({ timestamp: Date.now() });
                }

                // The session demonstrably works, so give back the one
                // sign-in-again attempt: an expiry later in this page load is a
                // new problem and deserves its own bounce.
                resetReauthAttempts();
                setUserEntities(entities);

                // Restore selected entity from localStorage or default to the first one
                const storedEntityId = localStorage.getItem('selectedManagedEntityId');
                let entitySet = false;

                if (storedEntityId) {
                    const found = entities.find((e) => e.id === storedEntityId);
                    if (found) {
                        setCurrentEntity(found);
                        entitySet = true;
                    }
                }

                if (!entitySet && entities.length > 0) {
                    setCurrentEntity(entities[0]);
                    localStorage.setItem('selectedManagedEntityId', entities[0].id);
                }
            } catch (err: any) {
                const failure = classifyApiFailure(err);

                if (isAuthFailure(failure)) {
                    // The server does not recognise this session. Nothing is
                    // wrong with the API, so do not report one.
                    //
                    // The remedy — clear the stale cache, let the app send them
                    // to sign in — is spent AT MOST ONCE per page load, and the
                    // clear and the redirect are one action rather than two.
                    // Splitting them loops in both directions: clearing without
                    // redirecting strands a signed-out user on a console that
                    // cannot load, and redirecting without clearing hits
                    // AdminLogin's already-signed-in bounce and comes straight
                    // back. Latched because the 401 this was written for fires
                    // right after a SUCCESSFUL login, where an unlatched remedy
                    // is an infinite loop (see reauthAttempt.ts).
                    //
                    // Deliberately no navigation here. This provider is mounted
                    // by admin, the desktop operator console AND the marketing
                    // site, which do not share a login route — and a guest holds
                    // a real session, so shared code must never bounce an
                    // audience surface. Consumers read the exposed failure kind
                    // (and whether a cached user survived) and decide themselves.
                    const mayRetryAuth = shouldAttemptReauth();
                    if (mayRetryAuth) {
                        markReauthAttempted();
                        authService.clearLocalSession();
                    }
                    setUserEntities([]);
                    setCurrentEntity(null);
                    setError(
                        mayRetryAuth
                            ? 'We could not authenticate your session.'
                            : 'We could not authenticate your session, and signing in again did not help.',
                    );
                    setErrorKind(failure.kind);
                    return;
                }

                console.error('[ManagedEntityContext] Failed to fetch managed entities:', err);
                setError(err.message || 'Failed to load workspaces');
                setErrorKind(failure.kind);
            } finally {
                setIsLoading(false);
                setHasLoadedOnce(true);
            }
        };

        // `run()` swallows its own errors, so this promise always fulfils and
        // the slot is always released.
        const request = run().finally(() => { inFlight.current = null; });
        inFlight.current = request;
        return request;
    }, []);

    useEffect(() => {
        // Wait for UserProvider to finish loading before deciding auth state.
        // Without this, cross-origin token handoff (e.g. invitation claim redirect)
        // hits a race: token is in localStorage but user hasn't loaded yet,
        // so isAuthenticated is briefly false → entities stays empty → /get-started redirect.
        //
        // `fetchEntities` is dependency-free, so its identity is stable for the
        // life of the provider — this effect is re-armed only by a real change
        // in auth state, never by a re-created callback.
        if (userLoading) return;

        if (isAuthenticated) {
            fetchEntities();
        } else {
            setIsLoading(false);
            setHasLoadedOnce(true);
        }
    }, [isAuthenticated, userLoading, fetchEntities]);

    const refreshEntities = useCallback(async () => {
        await fetchEntities();
    }, [fetchEntities]);

    const switchEntity = useCallback((entityId: string) => {
        const found = userEntities.find((e) => e.id === entityId);
        if (found) {
            setCurrentEntity(found);
            localStorage.setItem('selectedManagedEntityId', found.id);
        }
    }, [userEntities]);

    const hasFeature = useCallback((feature: FeatureId): boolean => {
        if (!currentEntity) return false;
        return canAccessFeature(getEffectivePlan(currentEntity), feature);
    }, [currentEntity]);



    const contextValue = React.useMemo(() => ({
        currentEntity,
        currentProfile,
        entities: userEntities,
        userEntities,
        isLoading,
        hasLoadedOnce,
        error,
        errorKind,
        refreshEntities,
        switchEntity,
        setCurrentEntity,
        hasFeature,
    }), [currentEntity, currentProfile, userEntities, isLoading, hasLoadedOnce, error, errorKind, hasFeature, refreshEntities, switchEntity]);

    return (
        <ManagedEntityContext.Provider
            value={contextValue}
        >
            {children}
        </ManagedEntityContext.Provider>
    );
};

export const useManagedEntity = (): ManagedEntityContextType => {
    const context = useContext(ManagedEntityContext);
    if (context === undefined) {
        throw new Error('useManagedEntity must be used within a ManagedEntityProvider');
    }
    return context;
};
